import styles from "./styles.module.scss";

import { useState, useEffect, useRef } from "react";
import { OnNavigationType } from "../_basic/SlideNavigation";
import { AddressFormSlideType } from "../../../types/form";

interface AddressFormSlideProps {
  data: AddressFormSlideType;
  onNavigation: OnNavigationType;
  setPayloadValue?: any;
  sendAnalyticsData?: any;
}

declare const google: any;

const API_CALL_DELAY = 1000;

const AddressFormSlide: React.FC<AddressFormSlideProps> = ({
  data: slideData,
  setPayloadValue,
  onNavigation,
  sendAnalyticsData,
}) => {
  const [zipCode, setZipCode] = useState<string>("");
  const [cityInput, setCityInput] = useState<string>("");
  const [streetName, setStreetName] = useState<string>("");
  const [streetSuggestions, setStreetSuggestions] = useState<string[]>([]);
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [streetNumber, setStreetNumber] = useState<string>("");
  const [allFieldsFilled, setAllFieldsFilled] = useState<boolean>(false);
  const [isLoadingMap, setIsLoadingMap] = useState<boolean>(false);
  const [locationData, setLocationData] = useState<{
    exactMatch: boolean;
    coordinates: any;
    lat: string;
    lng: string;
  } | null>(null);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const lastInputAtTime = useRef<number>(0);

  const encodedCredentials = btoa(process.env.REACT_APP_USER_DETAILS || "");

  const fetchCityByZip = async (query: string) => {
    const response = await fetch(
      `https://app-addressvalidationservice-staging.azurewebsites.net/autocomplete/cities?query=${query}&zipCode=${zipCode}&countryCode=it`,
      {
        method: "GET",
        headers: {
          Authorization: `Basic ${encodedCredentials}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data?.cities?.length) {
        setCitySuggestions(data.cities || []);
      }
    } else {
      console.error(
        "Failed to fetch city:",
        response.status,
        response.statusText
      );
    }
  };

  const fetchStreetSuggestions = async (query: string) => {
    if (!zipCode || query.length < 1) return;

    const response = await fetch(
      `https://app-addressvalidationservice-staging.azurewebsites.net/autocomplete/street?query=${query}&zipCode=${zipCode}&city=${cityInput}&countryCode=it`,
      {
        method: "GET",
        headers: {
          Authorization: `Basic ${encodedCredentials}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      }
    );

    if (response.ok) {
      const data = await response.json();
      setStreetSuggestions(data.suggestions || []);
    } else {
      console.error(
        "Failed to fetch street suggestions:",
        response.status,
        response.statusText
      );
    }
  };

  const fetchCoordinates = async () => {
    setIsLoadingMap(true);
    const response = await fetch(
      `https://app-addressvalidationservice-staging.azurewebsites.net/geocoding/coordinates?street=${streetName}&streetNumber=${streetNumber}&zipCode=${zipCode}&city=${cityInput}`,
      {
        method: "GET",
        headers: {
          Authorization: `Basic ${encodedCredentials}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      }
    );

    if (response.ok) {
      const data = await response.json();
      setLocationData(data);
      setIsLoadingMap(false);
    }
  };

  const loadGoogleMapsScript = (callback: () => void) => {
    const existingScript = document.getElementById("googleMaps");

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?libraries=maps&key=${process.env.REACT_APP_GOOGLE_API_KEY}&loading=async&callback=initMap&v=weekly&map_ids=&language=en&region=US&auth_referrer_policy=origin`;
      script.id = "googleMaps";
      script.async = true;

      window.initMap = callback;

      script.onload = () => {
        callback();
      };

      script.onerror = () => {
        console.error("Error loading the Google Maps script");
      };

      document.body.appendChild(script);
    } else {
      callback();
    }
  };

  const initMap = () => {
    if (mapRef.current && locationData) {
      setIsLoadingMap(true);

      if (window.google && window.google.maps && window.google.maps.MapTypeId) {
        const map = new google.maps.Map(mapRef.current, {
          center: {
            lat: parseFloat(locationData.coordinates.lat),
            lng: parseFloat(locationData.coordinates.lng),
          },
          zoom: 18,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          disableDefaultUI: true,
          zoomControl: true,
          maxZoom: 20,
          minZoom: 15,
        });

        const marker = new google.maps.Marker({
          position: {
            lat: parseFloat(locationData.coordinates.lat),
            lng: parseFloat(locationData.coordinates.lng),
          },
          map,
          icon: {
            url: locationData.exactMatch
              ? "data:image/svg+xml;utf-8,<svg data-testid='geopicker-pin' xmlns='http://www.w3.org/2000/svg' width='35.272' height='49.795' viewBox='0 0 35.272 49.795'><defs><style>.cls-1{fill:rgb(71,186,28);}.cls-2{fill:rgb(255,255,255);}</style></defs><g id='Pin' transform='translate(0 -0.001)'><path id='Path_1700' data-name='Path 1700' class='cls-1' d='M35.272,17.637c0,12.506-17.636,32.159-17.636,32.159S0,30.143,0,17.637a17.636,17.636,0,0,1,35.272,0'/><path id='Path_1701' data-name='Path 1701' class='cls-2' d='M12.938,21.237a8.3,8.3,0,1,1,8.3,8.3,8.3,8.3,0,0,1-8.3-8.3' transform='translate(-3.601 -3.601)'/></g></svg>"
              : "data:image/svg+xml;utf-8, <svg data-testid='geopicker-pin' xmlns='http://www.w3.org/2000/svg' width='35.272' height='49.795' viewBox='0 0 35.272 49.795'> <defs> <style> .cls-1 { fill: rgb(255, 106, 0); } .cls-2 { fill: rgb(255, 255, 255); } </style> </defs> <g id='Pin' transform='translate(0 -0.001)'> <path id='Path_1700' data-name='Path 1700' class='cls-1' d='M35.272,17.637c0,12.506-17.636,32.159-17.636,32.159S0,30.143,0,17.637a17.636,17.636,0,0,1,35.272,0'/> <path id='Path_1701' data-name='Path 1701' class='cls-2' d='M12.938,21.237a8.3,8.3,0,1,1,8.3,8.3,8.3,8.3,0,0,1-8.3-8.3' transform='translate(-3.601 -3.601)'/> </g> </svg>",
            scaledSize: new google.maps.Size(35.272, 49.795),
          },
        });
        google.maps.event.addListener(marker, "dragend", (event: any) => {
          const newPosition = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };
          locationData.coordinates.lat = newPosition.lat;
          locationData.coordinates.lng = newPosition.lng;
          setLocationData({ ...locationData });
        });

        google.maps.event.addListenerOnce(map, "tilesloaded", () => {
          setIsLoadingMap(false);
        });
      } else {
        setIsLoadingMap(false);
      }
    }
  };

  useEffect(() => {
    if (zipCode.length !== 0) {
      setPayloadValue(`contact[${"zipcode"}]`, zipCode);
    }
  }, [zipCode]);
  useEffect(() => {
    if (cityInput.length !== 0) {
      setPayloadValue(`contact[${"city"}]`, cityInput);
    }
  }, [cityInput]);
  useEffect(() => {
    if (streetName.length !== 0) {
      setPayloadValue(`contact[${"street"}]`, streetName);
    }
  }, [streetName]);
  useEffect(() => {
    if (streetNumber.length !== 0) {
      setPayloadValue(`contact[${"street_number"}]`, streetNumber);
    }
  }, [streetNumber]);

  const handleSubmit = () => {
    setPayloadValue(`contact[${"zipcode"}]`, zipCode);
    setPayloadValue(`contact[${"city"}]`, cityInput);
    setPayloadValue(`contact[${"street"}]`, streetName);
    setPayloadValue(`contact[${"street_number"}]`, streetNumber);

    setPayloadValue(`contact[${"geocoordinates"}]`, {
      lat: locationData?.coordinates.lat,
      lng: locationData?.coordinates.lng,
    });

    sendAnalyticsData("contact_form", "zipCode");
    sendAnalyticsData("contact_form", "cityInput");
    sendAnalyticsData("contact_form", "streetName");
    sendAnalyticsData("contact_form", "streetNumber");
    sendAnalyticsData("contact_form", "geoCoordinates");
    sendAnalyticsData("address_form", "continue_button");

    onNavigation(1);
  };

  const handleSubmitWithOutCoordinates = () => {
    setPayloadValue(`contact[${"zipcode"}]`, zipCode);
    setPayloadValue(`contact[${"city"}]`, cityInput);
    setPayloadValue(`contact[${"street"}]`, streetName);
    setPayloadValue(`contact[${"street_number"}]`, streetNumber);
    sendAnalyticsData("contact_form", "zipCode");
    sendAnalyticsData("contact_form", "cityInput");
    sendAnalyticsData("contact_form", "streetName");
    sendAnalyticsData("contact_form", "streetNumber");
    sendAnalyticsData("address_form", "continue_button");

    onNavigation(1);
  };

  useEffect(() => {
    if (locationData) {
      loadGoogleMapsScript(initMap);
    }
  }, [locationData]);

  useEffect(() => {
    if (zipCode.length === 5) fetchCityByZip(citySuggestions[0]);
    else if (zipCode.length !== 5) {
      setCityInput("");
      setStreetNumber("");
      setStreetName("");
      setStreetSuggestions([]);
      setCitySuggestions([]);
    }
  }, [zipCode]);

  useEffect(() => {
    lastInputAtTime.current = Date.now();
    const isComplete =
      !!zipCode && !!cityInput && !!streetName && !!streetNumber;
    setAllFieldsFilled(isComplete);

    if (isComplete) {
      setTimeout(() => {
        const nowTime = Date.now();
        if (nowTime - lastInputAtTime.current >= API_CALL_DELAY) {
          fetchCoordinates();
        }
      }, API_CALL_DELAY);
    }
  }, [zipCode, cityInput, streetName, streetNumber]);

  useEffect(() => {
    if (citySuggestions.length === 1) {
      setCityInput(citySuggestions[0]);
    }
  }, [citySuggestions, setCityInput]);

  return (
    <div className={styles.addressFormContainer}>
      <div className={styles.title}>{slideData.title}</div>

      <div className={styles.inputGroup}>
        <label className={styles.labels} htmlFor="zipCode">
          {slideData.zipCodeInput.label}
        </label>
        <input
          className={styles.inputField}
          type="text"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          id="zipCode"
          maxLength={5}
          placeholder={slideData.zipCodeInput.placeholder || ""}
        />
      </div>

      <div className={styles.cityInputField}>
        <div className={styles.inputGroup}>
          <label className={styles.labels} htmlFor="citySelect">
            {slideData.cityInput?.label}
          </label>
          <select
            id="citySelect"
            className={`${styles.inputField} ${
              citySuggestions.length === 0 && styles.cityField
            } ${styles.customSelect}`}
            onChange={(e) => {
              setCityInput(e.target.value);
              setStreetName("");
              setStreetSuggestions([]);
              setStreetNumber("");
            }}
            value={cityInput}>
            <option value="" disabled>
              {citySuggestions.length > 0
                ? slideData?.cityInput?.select
                : slideData.cityInput?.placeholder}
            </option>
            {citySuggestions.map((suggestion, index) => (
              <option key={index} value={suggestion}>
                {suggestion}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`${styles.streetContainer}`}>
        <div className={`${styles.inputGroup} ${styles.inputGroupStreet}`}>
          <label className={styles.labels} htmlFor="streetName">
            {slideData.streetInput?.label}
          </label>
          <input
            className={`${styles.streetNameField} ${styles.inputField} ${
              zipCode.length !== 5 && styles.cityField
            }`}
            type="text"
            placeholder={slideData.streetInput?.placeholder}
            onChange={(e) => {
              setStreetName(e.target.value);
              fetchStreetSuggestions(e.target.value);
            }}
            value={streetName}
          />
          {streetSuggestions.length > 0 && streetName.length > 0 && (
            <ul className={`${styles.suggestionList}`}>
              {streetSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => {
                    setStreetName(suggestion);
                    setStreetSuggestions([]);
                  }}
                  className={styles.dropdown}>
                  {suggestion}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.labels} htmlFor="streetNumber">
            {slideData.streetNumberInput?.label}
          </label>
          <input
            className={`${styles.streetNumberField} ${styles.inputField} ${
              zipCode.length !== 5 && styles.cityField
            }`}
            type="text"
            placeholder={slideData.streetNumberInput?.placeholder}
            onChange={(e) => setStreetNumber(e.target.value)}
            value={streetNumber}
          />
        </div>
      </div>
      {slideData.geopicker && (
        <>
          <div
            className={` 
            ${styles.geoPickerText} ${
              !(
                !allFieldsFilled ||
                !locationData ||
                (locationData && locationData?.exactMatch)
              ) && styles.coloredText
            } ${(!allFieldsFilled || isLoadingMap) && styles.blurredText} `}>
            {!allFieldsFilled ||
            !locationData ||
            (locationData && locationData?.exactMatch)
              ? slideData.geopicker.title.exactCoordinatesFound
              : slideData.geopicker.title.exactCoordinatesNotFound}
          </div>
          <div
            className={`${styles.loaderContainer}`}
            style={{
              background: `url(https://slider-assets.cust.enpal.io/slider-images/google-maps-placeholder.png)`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
            }}>
            {allFieldsFilled && isLoadingMap && (
              <div className={styles.loader}></div>
            )}
            {allFieldsFilled && (
              <div ref={mapRef} className={styles.googleMap}></div>
            )}
          </div>
        </>
      )}
      {slideData.geopicker?.alternativeButtonText && allFieldsFilled && (
        <div
          className={styles.skipButton}
          onClick={handleSubmitWithOutCoordinates}>
          {slideData.geopicker?.alternativeButtonText.skipGeopicker}
        </div>
      )}
      {slideData?.navigation && (
        <button
          type="button"
          className={`${
            allFieldsFilled ? styles.submitButton : styles.disabledButton
          }`}
          disabled={!allFieldsFilled}
          onClick={handleSubmit}>
          {slideData?.navigation.nextButtonLabel || "Submit Address"}
        </button>
      )}
    </div>
  );
};

export default AddressFormSlide;
